import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, Button,
  CircularProgress, Alert, Divider, Tooltip, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import billingApi from '../services/billingApi';

/**
 * Entity-agnostic actions dialog.
 *
 * Fetches eligible workflows for any entity type via
 * GET /api/admin/{entityType}s/:id/actions
 * and renders available/unavailable action buttons.
 *
 * Props:
 *   open, onClose          — dialog state
 *   entityType             — 'subscription', 'customer', 'payment', etc.
 *   entityId               — the entity's ID
 *   apiPath                — override the default API path (optional)
 *   title                  — dialog title (default: "{EntityType} Actions")
 *   renderSummary(entity)  — render function for the entity summary card
 *   extraActions            — additional non-workflow buttons (e.g., Retry Payment)
 *   onRunWorkflow(workflowId, entityId) — called when a workflow button is clicked
 */
export default function EntityActionsDialog({
  open, onClose, entityType, entityId,
  apiPath, title, renderSummary, extraActions,
  onRunWorkflow,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && entityId) {
      loadActions();
    } else {
      setData(null);
      setError('');
    }
  }, [open, entityId]);

  async function loadActions() {
    setLoading(true);
    setError('');
    try {
      const path = apiPath || `/api/admin/${entityType}s/${entityId}/actions`;
      const result = await billingApi.get(path);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const entity = data?.[entityType] || data?.entity;
  const workflows = data?.workflows || [];
  const eligible = workflows.filter(w => w.eligible);
  const ineligible = workflows.filter(w => !w.eligible);

  const dialogTitle = title || `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} Actions`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        {dialogTitle}
        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {entity && !loading && (
          <>
            {/* Entity summary — provided by caller */}
            {renderSummary && renderSummary(entity)}

            {/* Eligible workflows */}
            {(eligible.length > 0 || extraActions) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Available Actions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {eligible.map(w => (
                    <Button
                      key={w.workflowId}
                      variant="outlined"
                      color={w.color || 'inherit'}
                      size="small"
                      onClick={() => onRunWorkflow(w.workflowId, entityId)}
                      sx={{ textTransform: 'none' }}
                    >
                      {w.label}
                    </Button>
                  ))}
                  {extraActions && extraActions(entity)}
                </Box>
              </Box>
            )}

            {/* Ineligible workflows */}
            {ineligible.length > 0 && (
              <Box>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Unavailable
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {ineligible.map(w => (
                    <Tooltip key={w.workflowId} title={w.description || 'Not available'}>
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled
                          sx={{ textTransform: 'none' }}
                        >
                          {w.label}
                        </Button>
                      </span>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
